import { OtherFile } from './api';

export interface OtherFileCreateDTO {
  title: string;
  moduleType: string;
}

export interface OtherFileUploadDTO {
  title: string;
  moduleType: string;
  file: File;
}

const API_BASE_URL = 'https://localhost:7222/api';
const baseUrl = `${API_BASE_URL}/OtherFile`;

export const otherFileService = {
  // Get all other files
  async getAllOtherFiles(): Promise<OtherFile[]> {
    const token = localStorage.getItem('token');
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    };

    const response = await fetch(baseUrl, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      throw new Error('Failed to fetch other files');
    }

    return response.json();
  },

  // Get other files by module type
  async getOtherFilesByModuleType(moduleType: string): Promise<OtherFile[]> {
    const token = localStorage.getItem('token');
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    };

    const response = await fetch(`${baseUrl}/module/${encodeURIComponent(moduleType)}`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      throw new Error('Failed to fetch other files by module type');
    }

    return response.json();
  },

  // Get other file by ID
  async getOtherFileById(id: string): Promise<OtherFile> {
    const token = localStorage.getItem('token');
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    };

    const response = await fetch(`${baseUrl}/${id}`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      throw new Error('Failed to fetch other file');
    }

    return response.json();
  },

  // Upload other file
  async uploadOtherFile(otherFile: OtherFileUploadDTO): Promise<OtherFile> {
    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('title', otherFile.title);
    formData.append('moduleType', otherFile.moduleType);
    formData.append('file', otherFile.file);

    const headers: HeadersInit = {
      ...(token && { Authorization: `Bearer ${token}` }),
    };

    const response = await fetch(`${baseUrl}/upload`, {
      method: 'POST',
      headers,
      body: formData
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to upload other file: ${errorText}`);
    }

    return response.json();
  },

  // Update other file
  async updateOtherFile(id: string, title: string, file?: File): Promise<OtherFile> {
    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('title', title);
    if (file) {
      formData.append('file', file);
    }

    const headers: HeadersInit = {
      ...(token && { Authorization: `Bearer ${token}` }),
    };

    const response = await fetch(`${baseUrl}/${id}`, {
      method: 'PUT',
      headers,
      body: formData
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to update other file: ${errorText}`);
    }

    return response.json();
  },

  // Download other file
  async downloadOtherFile(id: string): Promise<void> {
    const token = localStorage.getItem('token');
    const headers: HeadersInit = {
      ...(token && { Authorization: `Bearer ${token}` }),
    };

    const response = await fetch(`${baseUrl}/${id}/download`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      throw new Error('Failed to download file');
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `otherfile-${id}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  },

  // Show other file (view in browser)
  async showOtherFile(id: string): Promise<void> {
    const token = localStorage.getItem('token');
    const headers: HeadersInit = {
      ...(token && { Authorization: `Bearer ${token}` }),
    };

    const response = await fetch(`${baseUrl}/${id}/download`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      throw new Error('Failed to show file');
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    window.open(url, '_blank');
  },

  // Delete other file
  async deleteOtherFile(id: string): Promise<void> {
    const token = localStorage.getItem('token');
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    };

    const response = await fetch(`${baseUrl}/${id}`, {
      method: 'DELETE',
      headers,
    });

    if (!response.ok) {
      throw new Error('Failed to delete other file');
    }
  },

  // Get module types (fixed types for Other Files)
  getModuleTypes(): string[] {
    return [
      'Investor Charter - Broking',
      'Investor Complaint', 
      'AP Branch Details',
      'KMP Details'
    ];
  },

  // Format file size
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  },

  // Format date
  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }
}; 